import { Box, Text } from '@shopify/polaris';
import { useMemo, useState } from 'react';

// Paleta pastel — misma que el back/PDF y el radar de eficacia.
const PALETTE = ['#a4c2d8', '#d8b4a4', '#a4c8a8', '#C9CCCF', '#b8a4d8', '#d8c4a4', '#a4d8c8', '#d8a4b4'];

const INK = '#1F3A5F';
const SUBTLE = '#6B7280';
const GRID = '#E1E3E5';

// Formato corto del eje X — "abr 25", "may 25" — Polaris-friendly.
const formatMonth = (ym) => {
  const [y, m] = ym.split('-');
  const names = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
  return `${names[parseInt(m, 10) - 1]} ${y.slice(2)}`;
};

const formatY = (n, mode) => {
  if (mode === 'units') return new Intl.NumberFormat('es-ES', { maximumFractionDigits: 0 }).format(n);
  // money — compacto: 1.2K, 1.4M
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return new Intl.NumberFormat('es-ES', { maximumFractionDigits: 0 }).format(n);
};

/**
 * Line chart con N series — una línea por categoría. Eje X = meses
 * (YYYY-MM ordenado), eje Y = uds o €. SVG nativo, sin librerías.
 *
 *   monthly = { months: ['2025-05', ...], byCategory: [{ category, sellUnits: [], sellValuePvf: [] }] }
 *
 * Sólo muestra las top-N categorías por suma para no saturar (en el
 * radar también funcionamos así); las que quedan fuera se acumulan en
 * un "Resto" en gris suave.
 */
export const MonthlyTrendChart = ({ monthly, mode = 'units', topN = 6 }) => {
  const [hoverIdx, setHoverIdx] = useState(null);

  const { lines, maxY } = useMemo(() => {
    if (!monthly || !monthly.byCategory.length) return { lines: [], maxY: 0 };
    const valuesKey = mode === 'units' ? 'sellUnits' : 'sellValuePvf';

    // Ordenamos categorías por suma DESC y partimos en top-N + resto.
    const enriched = monthly.byCategory.map((c) => ({
      category: c.category,
      values: c[valuesKey],
      total: c[valuesKey].reduce((a, b) => a + b, 0),
    }));
    enriched.sort((a, b) => b.total - a.total);

    const top = enriched.slice(0, topN);
    const rest = enriched.slice(topN);

    const series = top.map((s, i) => ({ category: s.category, values: s.values, color: PALETTE[i % PALETTE.length] }));

    if (rest.length > 0) {
      const restValues = monthly.months.map((_, idx) =>
        rest.reduce((acc, s) => acc + (s.values[idx] || 0), 0),
      );
      series.push({ category: `Resto (${rest.length})`, values: restValues, color: GRID });
    }

    const allVals = series.flatMap((s) => s.values);
    const max = allVals.length ? Math.max(...allVals) : 0;
    return { lines: series, maxY: max };
  }, [monthly, mode, topN]);

  if (!monthly || !monthly.months.length || lines.length === 0) {
    return (
      <Box padding="4">
        <Text variant="bodyMd" tone="subdued" alignment="center" as="p">
          Sin datos para la evolución mensual.
        </Text>
      </Box>
    );
  }

  const months = monthly.months;
  const W = 720;
  const H = 280;
  const padLeft = 50;
  const padRight = 140;
  const padTop = 16;
  const padBottom = 32;
  const innerW = W - padLeft - padRight;
  const innerH = H - padTop - padBottom;

  const xAt = (i) => padLeft + (months.length === 1 ? innerW / 2 : (i / (months.length - 1)) * innerW);
  const yAt = (v) => padTop + innerH - (maxY > 0 ? (v / maxY) * innerH : 0);

  // 4 gridlines horizontales en proporciones 0, 0.25, 0.5, 0.75, 1.0.
  const gridSteps = [0, 0.25, 0.5, 0.75, 1.0];

  return (
    <Box>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        height="auto"
        style={{ display: 'block' }}
        onMouseLeave={() => setHoverIdx(null)}
      >
        {/* Gridlines + Y labels */}
        {gridSteps.map((t) => {
          const y = padTop + innerH - t * innerH;
          const v = t * maxY;
          return (
            <g key={t}>
              <line x1={padLeft} x2={padLeft + innerW} y1={y} y2={y} stroke={GRID} strokeWidth={0.5} />
              <text x={padLeft - 6} y={y + 3} textAnchor="end" fontSize="9" fill={SUBTLE}>
                {formatY(v, mode)}
              </text>
            </g>
          );
        })}

        {/* X labels (meses) */}
        {months.map((ym, i) => (
          <text key={ym} x={xAt(i)} y={H - 12} textAnchor="middle" fontSize="9" fill={SUBTLE}>
            {formatMonth(ym)}
          </text>
        ))}

        {/* Líneas */}
        {lines.map((s) => {
          const d = s.values.map((v, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i)} ${yAt(v)}`).join(' ');
          return (
            <g key={s.category}>
              <path d={d} stroke={s.color} strokeWidth={1.6} fill="none" />
              {s.values.map((v, i) => (
                <circle key={i} cx={xAt(i)} cy={yAt(v)} r={2.5} fill={s.color} />
              ))}
            </g>
          );
        })}

        {/* Hover guide: línea vertical en el mes pasado */}
        {hoverIdx !== null && (
          <line
            x1={xAt(hoverIdx)}
            x2={xAt(hoverIdx)}
            y1={padTop}
            y2={padTop + innerH}
            stroke={INK}
            strokeWidth={0.5}
            strokeDasharray="3 3"
            opacity={0.4}
          />
        )}

        {/* Capa transparente para captar el hover */}
        {months.map((_, i) => (
          <rect
            key={i}
            x={xAt(i) - (innerW / months.length) / 2}
            y={padTop}
            width={innerW / months.length}
            height={innerH}
            fill="transparent"
            onMouseEnter={() => setHoverIdx(i)}
          />
        ))}

        {/* Leyenda a la derecha */}
        {lines.map((s, idx) => {
          const y = padTop + idx * 16;
          return (
            <g key={s.category}>
              <rect x={padLeft + innerW + 10} y={y} width={10} height={10} fill={s.color} />
              <text x={padLeft + innerW + 26} y={y + 9} fontSize="10" fill={INK}>
                {s.category}
              </text>
            </g>
          );
        })}
      </svg>

      {/* Tooltip: tras el SVG para no estorbar el flujo de eventos */}
      {hoverIdx !== null && (
        <Box padding="2" background="bg-subdued">
          <Text variant="bodySm" fontWeight="semibold" as="p">
            {formatMonth(months[hoverIdx])}
          </Text>
          {lines.map((s) => (
            <Text key={s.category} variant="bodySm" as="p">
              <span style={{ display: 'inline-block', width: 10, height: 10, background: s.color, marginRight: 6 }} />
              {s.category}: {formatY(s.values[hoverIdx], mode)}
            </Text>
          ))}
        </Box>
      )}
    </Box>
  );
};
